import {
	BASE_PRECISION_EXP,
	BigNum,
	PRICE_PRECISION_EXP,
	PerpMarketAccount,
	PositionDirection,
	SpotMarketAccount,
} from '@velocity-exchange/sdk';
import {
	MarketId,
	UIOrderType,
	TradeOffsetPrice,
	UIMarket,
	ENUM_UTILS,
} from '@velocity-exchange/common';
import { UserSettings } from '../../../../environmentVariables/EnvironmentVariableTypes';
import Env from '../../../../environmentVariables/EnvironmentVariables';
import { AuctionToastEvent } from '../../../../utils/DriftAppEvents';
import { isMajorMarket } from 'src/utils/market';
import { SignedMsgToastEvent } from 'src/@types/signedMsgOrders';
import { ITradeForm } from '../../types/tradeForm';
import { CommonTradeFormOutputOrderConstructionProps } from './types';

const getToastId = (
	marketId: MarketId,
	direction: PositionDirection,
	orderType: UIOrderType
) => {
	return `${marketId.key}-${orderType}-${ENUM_UTILS.toStr(
		direction
	)}-${Date.now()}`;
};

export const TRADE_PREP_UTILS = {
	getToastId,
	getAuctionToastEventProps: (props: {
		marketId: MarketId;
		direction: PositionDirection;
		baseSize: BigNum;
		numOrders: number;
		currentSettings: Partial<UserSettings>;
		bracketOrders: CommonTradeFormOutputOrderConstructionProps['bracketOrders'];
		orderType: CommonTradeFormOutputOrderConstructionProps['orderType'];
		userOrderId: number;
	}) => {
		const auctionToastEventProps: AuctionToastEvent = {
			v2Props: {
				marketId: MarketId.createPerpMarket(props.marketId.marketIndex),
				direction: props.direction,
				baseAmountOrdered: props.baseSize.val,
				numOrders: props.numOrders,
				auctionEnabled:
					(props.currentSettings?.auctionDuration ?? 0) > 0 &&
					!props.currentSettings?.placeAndTakeEnabled,
				includesSlOrder: !!props.bracketOrders?.stopLoss,
				includesTpOrder: !!props.bracketOrders?.takeProfit,
				userOrderId: props.userOrderId,
			},
			toastId: getToastId(
				props.marketId,
				props.direction,
				props.orderType as UIOrderType
			),
		};

		return auctionToastEventProps;
	},
	getSignedMsgOrderToastEventProps: (props: {
		marketId: MarketId;
		direction: PositionDirection;
		baseSize: BigNum;
		numOrders: number;
		currentSettings: Partial<UserSettings>;
		bracketOrders: CommonTradeFormOutputOrderConstructionProps['bracketOrders'];
		orderType: CommonTradeFormOutputOrderConstructionProps['orderType'];
		userOrderId: number;
		tradeFormDetails?: Partial<ITradeForm>;
	}) => {
		const signedMsgOrderToastEventProps: SignedMsgToastEvent = {
			orderProps: {
				marketId: MarketId.createPerpMarket(props.marketId.marketIndex),
				direction: props.direction,
				baseAmountOrdered: props.baseSize.val,
				numOrders: props.numOrders,
				includesSlOrder: !!props.bracketOrders?.stopLoss,
				includesTpOrder: !!props.bracketOrders?.takeProfit,
				userOrderId: props.userOrderId,
			},
			toastId: getToastId(
				props.marketId,
				props.direction,
				props.orderType as UIOrderType
			),
			originalTradeFormDetails: props.tradeFormDetails,
		};

		return signedMsgOrderToastEventProps;
	},
	perp: {
		formatTradeFormFieldValue: {
			baseSize: (input: string): BigNum => {
				return BigNum.fromPrint(input, BASE_PRECISION_EXP);
			},
			price: (input: string): BigNum => {
				return BigNum.fromPrint(input, PRICE_PRECISION_EXP);
			},
			secondaryPrice: (input: string): BigNum => {
				return BigNum.fromPrint(input, PRICE_PRECISION_EXP);
			},
		},
		adjustForStepSize: (
			baseSize: BigNum,
			perpMarketAccount: PerpMarketAccount
		) => {
			if (!perpMarketAccount) return baseSize;

			const stepSizeBn = perpMarketAccount.orderStepSize;
			const baseSizeBn = baseSize.val;
			const baseSizeAfterStep = baseSizeBn.div(stepSizeBn).mul(stepSizeBn);

			return BigNum.from(baseSizeAfterStep, BASE_PRECISION_EXP);
		},
	},
	spot: {
		formatTradeFormFieldValue: {
			baseSize: (
				input: string,
				spotMarketAccount: SpotMarketAccount
			): BigNum => {
				const basePrecisionToUse =
					UIMarket.spotMarkets[spotMarketAccount.marketIndex].precisionExp;

				return BigNum.fromPrint(input, basePrecisionToUse);
			},
			price: (input: string): BigNum => {
				return BigNum.fromPrint(input, PRICE_PRECISION_EXP);
			},
			secondaryPrice: (input: string): BigNum => {
				return BigNum.fromPrint(input, PRICE_PRECISION_EXP);
			},
		},
		adjustForStepSize: (
			baseSize: BigNum,
			spotMarketAccount: SpotMarketAccount
		) => {
			const basePrecisionToUse =
				UIMarket.spotMarkets[spotMarketAccount.marketIndex].precisionExp;

			const stepSizeBn = spotMarketAccount.orderStepSize;
			const baseSizeBn = baseSize.val;
			const baseSizeAfterStep = baseSizeBn.div(stepSizeBn).mul(stepSizeBn);

			return BigNum.from(baseSizeAfterStep, basePrecisionToUse);
		},
	},
	getMarketBasedAuctionStartPriceOffsetFrom: (
		settingsValue: TradeOffsetPrice | 'marketBased',
		marketId: MarketId
	): TradeOffsetPrice => {
		if (settingsValue !== 'marketBased') {
			return settingsValue;
		}

		return isMajorMarket(marketId) ? 'mark' : 'bestOffer';
	},
	getMarketBasedAuctionStartPriceOffset: (
		settingsValue: number | 'marketBased',
		marketId: MarketId
	): number => {
		if (settingsValue !== 'marketBased') {
			return settingsValue;
		}

		return isMajorMarket(marketId)
			? Env.marketBasedAuctionStartPriceOffsetMajor
			: Env.marketBasedAuctionStartPriceOffsetNonMajor;
	},
};
